import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {CloseHandlerService} from 'src/app/services/close-handler.service';
import {DialogService} from 'src/app/services/dialog.service';
import {AddTableModalComponent} from 'src/app/shared/ui-kits/table/add-table-modal/add-table-modal.component';
import {TableModalComponent} from 'src/app/shared/ui-kits/table/table-modal/table-modal.component';
import {
  additionalIcons,
  columnTypes,
  ITableRowAction,
  ITableValidation,
  ProfessionalTableType,
  UITableHeader,
} from 'src/app/shared/ui-kits/table/table.models';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {EmployeeService} from 'src/app/services/employee.service';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {
  ICertificate,
  IConference,
  IEnglishCourse,
  IInternalCourse,
  IInterviewee,
  IInterviewer,
  IMentorship,
  IPresentation,
  IProfessionalPut,
  IProfessionalTab,
  ITraining,
} from 'src/app/interfaces/professional-tab.interface';
import {IProfessionalEditMode} from 'src/app/interfaces/employee-edit-mode.interface';
import {ActivatedRoute} from '@angular/router';
import {AccessManagementService} from 'src/app/services/access-management.service';

import {
  IAssignedMentorTable,
  IProjectsTable,
} from 'src/app/interfaces/general-tables.interface';
import {Utils} from 'src/app/shared/utils/utils';
import {filter, switchMap, tap} from 'rxjs';
import {roles} from 'src/app/shared/utils/roles';
import {Tab} from 'src/app/interfaces/employee.interface';
import {ToastrService} from 'ngx-toastr';

type TrainingActionsData =
  | IConference[]
  | IEnglishCourse[]
  | IInternalCourse[]
  | IPresentation[]
  | ITraining[];

type NonTrainingActionsData =
  | ICertificate[]
  | IAssignedMentorTable[]
  | IInterviewee[];

/**
 * Component for professional data tab
 */
@UntilDestroy()
@Component({
  selector: 'app-professional-data',
  templateUrl: './professional-data.component.html',
  styleUrls: ['./professional-data.component.scss'],
})
export class ProfessionalDataComponent implements OnInit {
  public ProfessionalTableType = ProfessionalTableType;
  public typeOfTable = 'material-plus';
  public professionalDataForm!: UntypedFormGroup;
  public isEditable = false;

  public dataCert: ICertificate[] = [];

  public headersCert: UITableHeader[] = [
    {key: 'certificationName', index: 0, isEditable: true},
    {key: 'date', index: 1, type: columnTypes.datePicker, isEditable: true},
    {key: 'status', index: 2, type: columnTypes.dropDown, isEditable: true},
    {
      key: 'expirationDate',
      index: 3,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'employeeId', index: 4, notVisible: true},
    {key: 'id', index: 5, notVisible: true},
  ];

  /**
   * Headers and array for Conferences table
   */
  public dataConf: IConference[] = [];

  public headersConf: UITableHeader[] = [
    {key: 'trainingId', index: 3, notVisible: true},
    {
      key: 'conferenceName',
      index: 0,
      isEditable: true,
      type: columnTypes.dropDown,
    },
    {key: 'date', index: 1, type: columnTypes.datePicker},
    {key: 'status', index: 2, isEditable: true, type: columnTypes.dropDown},
    {key: 'employeeId', index: 4, notVisible: true},
    {key: 'id', index: 5, notVisible: true},
  ];

  /**
   * Headers and array for the Mentorship of others table
   */
  public dataMentor: IMentorship[] = [];

  public headersMentor: UITableHeader[] = [
    {key: 'menteeName', index: 0},
    {key: 'startDate', index: 1, type: columnTypes.datePicker},
    {key: 'endDate', index: 2, type: columnTypes.datePicker},
    {key: 'status', index: 3, type: columnTypes.dropDown},
    {key: 'menteeId', index: 4, notVisible: true},
    {key: 'employeeId', index: 4, notVisible: true},
    {key: 'id', index: 5, notVisible: true},
  ];

  /**
   * Headers and array for the Tech interview(Interviewee) table
   */
  public dataInterviewInterviewee: IInterviewee[] = [];

  public headersInterviewInterviewee: UITableHeader[] = [
    {
      key: 'interviewers',
      index: 1,
      type: columnTypes.dropDown,
      isMultiDropdown: true,
      isEditable: true,
    },
    {key: 'date', index: 2, type: columnTypes.datePicker, isEditable: true},
    {key: 'status', index: 3, type: columnTypes.dropDown, isEditable: true},
    {
      key: 'comment',
      index: 4,
      icons: additionalIcons.addReport,
      isEditable: true,
    },
    {key: 'candidateName', index: 0, notVisible: true},
    {key: 'employeeId', index: 6, notVisible: true},
    {key: 'id', index: 7, notVisible: true},
    {key: 'file', index: 5, type: columnTypes.link, isEditable: true},
  ];

  /**
   * Headers and array for the Tech interview(Interviewer) table
   */
  public dataInterviewInterviewer: IInterviewer[] = [];

  public headersInterviewInterviewer: UITableHeader[] = [
    {key: 'intervieweeFullName', index: 0},
    {key: 'startDate', index: 1, type: columnTypes.datePicker},
    {key: 'status', index: 2, type: columnTypes.dropDown},
    {key: 'comment', index: 3, icons: additionalIcons.addReport},
  ];

  /**
   * Headers and array for the Internal courses tables
   */
  public dataCourses: IInternalCourse[] = [];

  public headersCourses: UITableHeader[] = [
    {key: 'trainingId', index: 6, notVisible: true},
    {
      key: 'courseName',
      index: 0,
      isEditable: true,
      type: columnTypes.dropDown,
    },
    {key: 'startDate', index: 1, type: columnTypes.datePicker},
    {key: 'endDate', index: 2, type: columnTypes.datePicker},
    {key: 'result', index: 3, isEditable: true},
    {key: 'employeeId', index: 4, notVisible: true},
    {key: 'id', index: 5, notVisible: true},
  ];

  /**
   * Headers and array for the Presentation table
   */
  public dataPresentation: IPresentation[] = [];

  public headersPresentation: UITableHeader[] = [
    {key: 'assignedEmployees', index: 5, notVisible: true},
    {
      key: 'presentationName',
      index: 0,
      type: columnTypes.dropDown,
      isEditable: true,
    },
    {
      key: 'dateOfAttendance',
      index: 1,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {
      key: 'dateOfDelivery',
      index: 2,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'id', index: 4, notVisible: true},
    {key: 'type', index: 4, notVisible: true},
  ];

  /**
   * Headers and array for the Training table
   */
  public dataTraining: ITraining[] = [];

  public headersTraining: UITableHeader[] = [
    {key: 'trainingId', index: 5, notVisible: true},
    {
      key: 'name',
      index: 0,
      type: columnTypes.dropDown,
      isEditable: true,
    },
    {
      key: 'dateOfAttendance',
      index: 1,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {
      key: 'dateOfDelivery',
      index: 2,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'employeeId', index: 3, notVisible: true},
    {key: 'id', index: 4, notVisible: true},
  ];

  public itemsStacksMain: IDropdownObject[] = [];
  public itemsStacksSecondary: IDropdownObject[] = [];

  /**
   * Headers and array for the English courses table
   */
  public coursesData: IEnglishCourse[] = [];

  public coursesHeaders: UITableHeader[] = [
    {key: 'trainingId', index: 6, notVisible: true},
    {key: 'name', index: 0, type: columnTypes.dropDown, isEditable: true},
    {
      key: 'dateOfAttendance',
      index: 1,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {
      key: 'dateOfDelivery',
      index: 2,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'role', index: 3, type: columnTypes.dropDown, isEditable: true},
    {key: 'employeeId', index: 4, notVisible: true},
    {key: 'id', index: 5, notVisible: true},
  ];

  public dataAssignedMentor: IAssignedMentorTable[] = [];

  public assignedMentorHeaders: UITableHeader[] = [
    {key: 'mentor', index: 0, type: columnTypes.dropDown, isEditable: true},
    {key: 'start', index: 1, type: columnTypes.datePicker, isEditable: true},
    {
      key: 'endDate',
      index: 2,
      type: columnTypes.datePicker,
      isEditable: true,
    },
    {key: 'employeeId', index: 3, notVisible: true},
    {key: 'id', index: 4, notVisible: true},
  ];

  public dataProjects: IProjectsTable[] = [];

  public projectsHeaders: UITableHeader[] = [
    {key: 'name', index: 0},
    {key: 'pm', index: 1},
    {key: 'pmId', index: 2, notVisible: true},
    {key: 'id', index: 3, notVisible: true},
  ];

  tableValidations: Record<ProfessionalTableType, ITableValidation[]> = {
    [ProfessionalTableType.Certifications]: [
      {
        controlName: 'certificationName',
        validators: [Validators.required, Validators.maxLength(30)],
      },
      {
        controlName: 'date',
        validators: [Validators.required],
      },
      {
        controlName: 'status',
        validators: [Validators.required],
      },
    ],
    [ProfessionalTableType.AssignedMentors]: [
      {
        controlName: 'mentor',
        validators: [Validators.required, Validators.maxLength(30)],
      },
      {
        controlName: 'start',
        validators: [Validators.required],
      },
    ],
    [ProfessionalTableType.TechInterview]: [
      {
        controlName: 'interviewers',
        validators: [Validators.required],
      },
      {
        controlName: 'date',
        validators: [Validators.required],
      },
      {
        controlName: 'status',
        validators: [Validators.required],
      },
      {
        controlName: 'comment',
        validators: [Validators.maxLength(200)],
      },
    ],
    [ProfessionalTableType.EnglishCourses]: [
      {
        controlName: 'name',
        validators: [Validators.required],
      },
      {
        controlName: 'dateOfAttendance',
        validators: [Validators.required],
      },
    ],
    [ProfessionalTableType.Conference]: [
      {
        controlName: 'conferenceName',
        validators: [Validators.required],
      },
      {
        controlName: 'status',
        validators: [Validators.required],
      },
    ],
    [ProfessionalTableType.InternalCourses]: [
      {
        controlName: 'courseName',
        validators: [Validators.required],
      },
      {
        controlName: 'result',
        validators: [Validators.maxLength(200)],
      },
    ],
    [ProfessionalTableType.Presentation]: [
      {
        controlName: 'presentationName',
        validators: [Validators.required],
      },
      {
        controlName: 'dateOfAttendance',
        validators: [Validators.required],
      },
    ],
    [ProfessionalTableType.Training]: [
      {
        controlName: 'name',
        validators: [Validators.required],
      },
      {
        controlName: 'dateOfAttendance',
        validators: [Validators.required],
      },
    ],
  };

  public englishLevelGrammarOptions: IDropdownObject[] = [];
  public englishLevelSpeakingOptions: IDropdownObject[] = [];
  public statusOptions: IDropdownObject[] = [];
  public attemptsOptions: IDropdownObject[] = [
    {id: 1, name: 'One'},
    {id: 2, name: 'Two'},
    {id: 3, name: 'Three'},
  ];
  public trainingRoles: IDropdownObject[] = [];
  public changed = false;
  public formValueDefault!: IProfessionalPut;
  public isMentor?: boolean = false;
  public isInterviewer?: boolean = false;
  public employeeId!: number;
  public firstLoad = true;
  public isAccountant!: boolean;
  public isHrManager!: boolean;
  public isAdmin!: boolean;
  public isCEO!: boolean;
  public ableToEdit: boolean = true;
  public frameworks: IDropdownObject[] = [];
  public allDropdowns!: IProfessionalEditMode;
  public isEqualObjects = Utils.isEqualObjects;
  public isFieldInvalid = Utils.isFieldInvalid;
  public createEmptyObject = Utils.createEmptyObject;
  public isMentorChanged = false;
  @ViewChild('saveButton') saveButton!: ElementRef;

  constructor(
    private _fb: UntypedFormBuilder,
    private dialogService: DialogService,
    private closeHandler: CloseHandlerService,
    private employeeService: EmployeeService,
    private route: ActivatedRoute,
    private access: AccessManagementService,
    private notifier: ToastrService
  ) {}

  ngOnInit(): void {
    this.employeeId = +this.route.parent?.snapshot.params['id'];
    this.professionalDataForm = this._fb.group({
      techStackMain: new UntypedFormControl(null),
      techStackSecondary: new UntypedFormControl([]),
      frameworks: new UntypedFormControl(''),
      englishLevelGrammar: new UntypedFormControl(null, [Validators.required]),
      englishLevelSpeaking: new UntypedFormControl(null, [Validators.required]),
      securityTrainingResult: new UntypedFormControl('', [
        Validators.maxLength(20),
      ]),
      securityTrainingStatus: new UntypedFormControl(null),
      ammountOfAttempts: new UntypedFormControl(null),
      id: new UntypedFormControl(null),
      isMentor: new UntypedFormControl(null),
      isInterviewer: new UntypedFormControl(null),
    });

    this.employeeService
      .getTabData<IProfessionalTab>(Tab.Professional, this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe((data: IProfessionalTab) => {
        this.professionalDataForm.patchValue(data, {emitEvent: false});
        this.buildTable(data);
        this.isMentor = data.isMentor;
        this.isInterviewer = data.isInterviewer;
        if (data.techStackMain) {
          this.itemsStacksMain = [data.techStackMain];
        }
        if (data.techStackSecondary) {
          this.itemsStacksSecondary = data.techStackSecondary;
        }
        if (data.englishLevelGrammar) {
          this.englishLevelGrammarOptions = [data.englishLevelGrammar];
        }
        if (data.englishLevelSpeaking) {
          this.englishLevelSpeakingOptions = [data.englishLevelSpeaking];
        }
        if (data.securityTrainingStatus) {
          this.statusOptions = [data.securityTrainingStatus];
        }
        if (!data.ammountOfAttempts) {
          this.professionalDataForm.controls['ammountOfAttempts'].setValue(
            null
          );
        } else {
          this.attemptsOptions.forEach((el: IDropdownObject) => {
            if (el.id == data.ammountOfAttempts) {
              this.professionalDataForm.controls['ammountOfAttempts'].setValue(
                el
              );
              return;
            }
          });
        }
        if (data.frameworks) {
          this.frameworks = data.frameworks;
        }
        setTimeout(
          () => (this.formValueDefault = this.professionalDataForm.value)
        );
      });

    this.closeHandler.notSaveForm.pipe(untilDestroyed(this)).subscribe(() => {
      this.changed = true;
      this.professionalDataForm.setValue(this.formValueDefault);
      this.changed = false;
      this.isEditable = false;
      this.professionalDataForm.markAsUntouched();
    });

    this.professionalDataForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        if (
          value &&
          this.isEditable &&
          !this.changed &&
          !this.isEqualObjects(value, this.formValueDefault)
        ) {
          this.closeHandler.notSaved = true;
        }
      });

    this.isAccountant = this.access.checkPermissions(
      roles.ACCOUNTANT_DEPARTMENT
    );
    this.isAdmin = this.access.checkPermissions(roles.ADMIN);

    if (this.isAccountant || this.isAdmin) {
      this.ableToEdit = false;
    }
    this.isHrManager = this.access.checkPermissions(roles.HR_DEPARTMENT);
    this.isCEO = this.access.checkPermissions(roles.CEO);

    this.closeHandler.clickSavePopup
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.saveButton.nativeElement.click();
      });

    this.isMentorControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(mentorSelected => {
        this.isMentorChanged = true;
        this.isMentor = mentorSelected;
      });

    this.isInterviewerControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(
        interviewerSelected => (this.isInterviewer = interviewerSelected)
      );
  }

  switchEdit() {
    this.isEditable = !this.isEditable;
    if (this.isEditable) {
      if (this.firstLoad) {
        this.employeeService
          .getDropdownItems<IProfessionalEditMode>(Tab.Professional)
          .pipe(untilDestroyed(this))
          .subscribe((data: IProfessionalEditMode) => {
            this.allDropdowns = data;
            this.itemsStacksMain = data.StackTechnology;
            this.itemsStacksSecondary = data.StackTechnology;
            this.englishLevelGrammarOptions = data.EnglishLevel;
            this.englishLevelSpeakingOptions = data.EnglishLevel;
            this.statusOptions = data.EventStatus;
            this.frameworks = data.Framework;
            this.trainingRoles = data.TrainingRole;
            this.firstLoad = false;
          });
      }
    }
  }

  buildTable(data: IProfessionalTab) {
    this.dataCert = data.certificates;
    this.dataConf = data.conferences;
    this.dataCourses = data.internalCourses;
    this.dataInterviewInterviewee = data.intervieweeInterviews;
    this.dataInterviewInterviewer = data.interviewerInterviews;
    this.dataMentor = data.mentorships;
    this.dataPresentation = data.presentations;
    this.dataTraining = data.trainings;
    this.coursesData = data.englishCourses;
    this.dataAssignedMentor = data.assignedMentors;
    this.dataProjects = data.projects;
  }

  addNewTableData(headers: UITableHeader[], tableName: ProfessionalTableType) {
    this.dialogService
      .open(AddTableModalComponent, {
        data: {
          content: this.createEmptyObject(headers),
          headers,
          dropdown: this.allDropdowns,
          table: tableName,
          validation: this.tableValidations[tableName],
          employeeId: this.employeeId,
        },
        settings: {
          width: '500px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data =>
          this.employeeService.addNewProfessionalTableData(
            tableName,
            data,
            this.employeeId
          )
        )
      )
      .subscribe(result => this.buildTable(result));
  }

  updateTableData(
    modalData: NonTrainingActionsData,
    headers: UITableHeader[],
    tableName: ProfessionalTableType,
    index: number
  ) {
    this.dialogService
      .open(TableModalComponent, {
        data: {
          content: modalData[index],
          headers,
          dropdown: this.allDropdowns,
          table: tableName,
          validation: this.tableValidations[tableName],
        },
        settings: {
          width: '500px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data =>
          this.employeeService.updateProfessionalTables(
            tableName,
            data,
            this.employeeId
          )
        )
      )
      .subscribe(result => this.buildTable(result));
  }

  nonTrainingTableActions(
    event: ITableRowAction,
    data: NonTrainingActionsData,
    headers: UITableHeader[],
    tableName: ProfessionalTableType
  ) {
    switch (event.action) {
      case 'add':
        this.addNewTableData(headers, tableName);
        break;
      case 'edit':
        this.updateTableData(data, headers, tableName, event.index);
        break;
      default:
        break;
    }
  }

  updateTraining(
    tableName: ProfessionalTableType,
    index: number,
    modalData: TrainingActionsData,
    headers: UITableHeader[]
  ) {
    this.dialogService
      .open(TableModalComponent, {
        data: {
          content: modalData[index],
          dropdown: this.allDropdowns,
          table: tableName,
          headers,
          canDelete: true,
          validation: this.tableValidations[tableName],
        },
        settings: {
          width: '660px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => {
          if (data === 'delete')
            return this.employeeService.deleteTraining(
              modalData[index].id as number,
              this.employeeId
            );
          data.employeeId = this.employeeId;
          return this.employeeService.updateTraining(data, this.employeeId);
        })
      )
      .subscribe(response => this.buildTable(response));
  }

  addTraining(tableName: ProfessionalTableType, headers: UITableHeader[]) {
    this.dialogService
      .open(AddTableModalComponent, {
        data: {
          content: this.createEmptyObject(headers),
          dropdown: this.allDropdowns,
          table: tableName,
          headers,
          validation: this.tableValidations[tableName],
        },
        settings: {
          width: '660px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => {
          data.employeeId = this.employeeId;

          return this.employeeService.addTraining(data, this.employeeId);
        })
      )
      .subscribe(response => this.buildTable(response));
  }

  trainingActions(
    event: ITableRowAction,
    headers: UITableHeader[],
    data: TrainingActionsData,
    tableName: ProfessionalTableType
  ) {
    switch (event.action) {
      case 'add':
        this.addTraining(tableName, headers);
        break;
      case 'edit':
        this.updateTraining(tableName, event.index, data, headers);
        break;
    }
  }

  saveData() {
    if (this.professionalDataForm.invalid) {
      return Utils.validateAllFormFields(this.professionalDataForm);
    }
    this.formValueDefault = this.professionalDataForm.value;
    this.closeHandler.notSaved = false;
    this.employeeService
      .postTabData(Tab.Professional, this.professionalDataForm.value)
      .pipe(
        untilDestroyed(this),
        tap(() => {
          this.isEditable = !this.isEditable;
          this.notifier.success('Saved');
        }),
        filter(() => this.isMentorChanged),
        switchMap(() =>
          this.employeeService.getDropdownItems<IProfessionalEditMode>(
            Tab.Professional
          )
        )
      )
      .subscribe(dropdowns => {
        this.allDropdowns = dropdowns;
        this.isMentorChanged = false;
      });
  }

  get isMentorControl(): AbstractControl {
    return this.professionalDataForm.get('isMentor')!;
  }

  get isInterviewerControl(): AbstractControl {
    return this.professionalDataForm.get('isInterviewer')!;
  }
}
